/**
 * regionTypeLook: a region TYPE's appearance, one rule for every surface.
 *
 *   project.regionTypes[i] = { …, fillColor, fillOpacity, borderColor, strokeOpacity, lineStyle }
 *
 * `strokeOpacity` is the LINE's opacity, under the same name a markup and a
 * sub-region's own look (record.look, regionTarget.recordLookOf) use for it.
 *
 * The Regions page's Set Up and MainView's Regions panel both edit it through
 * RegionTypeLookEditor and save it through the granular region-types endpoint
 * (storage.saveRegionTypes: a server-side read-modify-write of only
 * settings.regionTypes, then a 'project' broadcast), so a change made on
 * either reaches the other, and every member, without a reload. The Region
 * tool (shared/regionTarget) draws a new part in it.
 *
 * It is the look of what is DRAWN NEXT. A part already on a drawing keeps its
 * own colours: it may have been styled on its own, and a state paints over
 * both when the part has one.
 *
 * Pure: no React, no storage.
 */

export const LINE_STYLES = [
  ['solid', 'Solid'],
  ['dashed', 'Dashed'],
  ['dotted', 'Dotted'],
  ['dashdot', 'Dash dot'],
  ['longdash', 'Long dash'],
];
const KNOWN_STYLES = new Set(LINE_STYLES.map(([id]) => id));

export const DEFAULT_LOOK = { fillColor: '#3498db', fillOpacity: 0.3, borderColor: '#3498db', strokeOpacity: 1, lineStyle: 'solid' };

/** A type's look with every field filled in ('none' is a real value: no fill / no line). */
export function typeLookOf(type) {
  const t = type || {};
  const fill = t.fillColor || t.borderColor || DEFAULT_LOOK.fillColor;
  const line = t.borderColor || t.fillColor || DEFAULT_LOOK.borderColor;
  const op = Number(t.fillOpacity);
  const lineOp = t.strokeOpacity == null ? NaN : Number(t.strokeOpacity);
  return {
    fillColor: fill,
    fillOpacity: Number.isFinite(op) ? Math.max(0, Math.min(1, op)) : DEFAULT_LOOK.fillOpacity,
    borderColor: line,
    strokeOpacity: Number.isFinite(lineOp) ? Math.max(0, Math.min(1, lineOp)) : DEFAULT_LOOK.strokeOpacity,
    lineStyle: KNOWN_STYLES.has(t.lineStyle) ? t.lineStyle : 'solid',
  };
}

/** The look as a MARKUP carries it (color = its line), for a part or a note. */
export function markupLookOf(type, { isPart = true } = {}) {
  if (!type) return {};
  const look = typeLookOf(type);
  const out = {};
  // No line on the type: the markup keeps the tool's line rather than being
  // given a colour of 'none' (a region with no outline cannot be picked up).
  if (look.borderColor !== 'none') out.color = look.borderColor;
  if (!isPart) return out;
  if (look.borderColor !== 'none') out.strokeOpacity = look.strokeOpacity;
  out.fillColor = look.fillColor;
  out.fillOpacity = look.fillOpacity;
  out.lineStyle = look.lineStyle;
  return out;
}

/** The types with one type's look changed (by id, else by name). Only look keys pass. */
/**
 * "Keep my markup options", a switch on the TYPE: drawing into its
 * sub-regions uses the person's own options row (no region colours, no state
 * colour), and a state never recolours its parts on the drawing. The state
 * is still recorded and shown in the panels.
 */
export const keepsMarkupOptions = (type) => !!(type && type.keepMarkupOptions);

export function typesWithLook(types, typeKey, patch) {
  const clean = {};
  for (const k of ['fillColor', 'fillOpacity', 'borderColor', 'strokeOpacity', 'lineStyle', 'keepMarkupOptions']) {
    if (patch && patch[k] !== undefined) clean[k] = patch[k];
  }
  return (types || []).map(t => (t && (t.id === typeKey || t.name === typeKey) ? { ...t, ...clean } : t));
}

/**
 * Whose look it is, a setting on the TYPE (chosen when it is made, changed
 * in its Set Up):
 *   'type'       (default)  one look for every sub-region of the type; a
 *                           change restyles every part already drawn
 *   'subRegion'             each sub-region keeps its own (record.look, laid
 *                           over the type's), edited in its record
 */
export const lookScopeOf = (type) => (type && type.lookScope === 'subRegion' ? 'subRegion' : 'type');

/**
 * A sub-region's own look (record.look, in MARKUP keys: color, strokeOpacity,
 * fillColor, fillOpacity, lineStyle) as the editor reads it (TYPE keys), over
 * its type's.
 */
export function ownLookForEditor(type, own) {
  const base = typeLookOf(type);
  const o = own && typeof own === 'object' ? own : {};
  return {
    ...base,
    ...(o.fillColor != null ? { fillColor: o.fillColor } : {}),
    ...(o.fillOpacity != null ? { fillOpacity: o.fillOpacity } : {}),
    ...(o.color != null ? { borderColor: o.color } : {}),
    ...(o.strokeOpacity != null ? { strokeOpacity: o.strokeOpacity } : {}),
    ...(o.lineStyle != null ? { lineStyle: o.lineStyle } : {}),
  };
}

/** An editor patch (TYPE keys) as a sub-region's own look patch (MARKUP keys). */
export function editorPatchToOwnLook(patch) {
  const out = {};
  if (!patch) return out;
  if (patch.fillColor !== undefined) out.fillColor = patch.fillColor;
  if (patch.fillOpacity !== undefined) out.fillOpacity = patch.fillOpacity;
  if (patch.borderColor !== undefined) out.color = patch.borderColor;
  if (patch.strokeOpacity !== undefined) out.strokeOpacity = patch.strokeOpacity;
  if (patch.lineStyle !== undefined) out.lineStyle = patch.lineStyle;
  return out;
}

/** An editor patch (TYPE keys) as a region SHAPE patch (shape keys), to restyle drawn parts. */
export function editorPatchToShape(patch) {
  const out = {};
  if (!patch) return out;
  for (const k of ['fillColor', 'fillOpacity', 'borderColor', 'strokeOpacity', 'lineStyle']) if (patch[k] !== undefined) out[k] = patch[k];
  return out;
}

/**
 * A SUB-REGION'S LOOK as a markup carries it (color, strokeOpacity,
 * fillColor, fillOpacity, lineStyle): its type's, then its own (record.look)
 * when the type gives each sub-region its own look. ONE rule for what is
 * drawn into a region, what is converted into one, the Appearance controls
 * and the swatches (regionTarget recordLookOf delegates here). A note (an
 * open markup kept with a region) takes the line colour only.
 */
export function subRegionLookOf(rec, type, { isPart = true } = {}) {
  const out = { ...markupLookOf(type, { isPart }) };
  if (isPart && out.strokeOpacity === undefined) out.strokeOpacity = 1;
  const own = lookScopeOf(type) === 'subRegion' && rec && rec.look && typeof rec.look === 'object' ? rec.look : null;
  const keys = isPart ? ['color', 'strokeOpacity', 'fillColor', 'fillOpacity', 'lineStyle'] : ['color'];
  if (own) for (const k of keys) if (own[k] !== undefined && own[k] !== null) out[k] = own[k];
  return out;
}

